import { CommonModule } from '@angular/common';
import {
  Component,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  Inject,
} from '@angular/core';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';
import { RecipientListInterface } from '@feature/dashboard/recipients/types/recipient-list';
import { SenderListInterface } from '@feature/dashboard/senders/types/sender-list';
import { MaterialModule } from '@sharedModule/material/material.module';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { ReplaySubject, Subject, take, takeUntil } from 'rxjs';
import { TransactionService } from '../../services/transaction.service';

interface ExistingUserOption {
  id: number;
  name: string;
  email: string;
  raw: SenderListInterface | RecipientListInterface;
}

/** Selected entity (sender or recipient); partial intersection so consumers can
 * read both `sender_id` and `bene_id`. Empty object when nothing was chosen. */
export type ExistingUserSelection = Partial<
  SenderListInterface & RecipientListInterface
>;

export interface ExistingUserDialogResult {
  isSender: boolean;
  data: ExistingUserSelection;
}

@Component({
  selector: 'app-existing-user',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    ReactiveFormsModule,
    NgxMatSelectSearchModule,
  ],
  templateUrl: './existing-user.component.html',
  styleUrl: './existing-user.component.scss',
})
export class ExistingUserComponent implements OnDestroy, AfterViewInit {
  private options: ExistingUserOption[] = [];

  public senderRecipientCtrl: FormControl = new FormControl({});
  public senderRecipientFilterCtrl: FormControl = new FormControl();
  public filteredOptions = new ReplaySubject<ExistingUserOption[]>(1);

  @ViewChild('singleSelect', { static: true }) singleSelect!: MatSelect;

  #destroy$ = new Subject<void>();

  constructor(
    private transactionService: TransactionService,
    @Inject(MAT_DIALOG_DATA)
    public dialogData: {
      popupType: boolean;
      sourceCountry: string;
      destinationCountry: string;
    },
    private closeDialogRef: MatDialogRef<ExistingUserComponent>
  ) {
    this.loadData();
  }

  get entityLabel(): string {
    return this.dialogData.popupType ? 'Sender' : 'Recipient';
  }

  private loadData(): void {
    if (this.dialogData.popupType) {
      this.transactionService
        .getSenderList({ sender_country: this.dialogData.sourceCountry })
        .pipe(takeUntil(this.#destroy$))
        .subscribe(res => {
          if (res.success)
            this.setOptions(res.response.map(this.toSenderOption));
        });
    } else {
      this.transactionService
        .getRecipientList({
          beneficiary_country: this.dialogData.destinationCountry,
        })
        .pipe(takeUntil(this.#destroy$))
        .subscribe(res => {
          if (res.success)
            this.setOptions(res.response.map(this.toRecipientOption));
        });
    }

    this.senderRecipientFilterCtrl.valueChanges
      .pipe(takeUntil(this.#destroy$))
      .subscribe(() => this.applyFilter());
  }

  private setOptions(opts: ExistingUserOption[]): void {
    this.options = opts;
    this.filteredOptions.next(opts.slice());
  }

  private toSenderOption = (item: SenderListInterface): ExistingUserOption => ({
    id: item.sender_id,
    name: [item.first_name, item.middle_name, item.last_name]
      .filter(Boolean)
      .join(' '),
    email: item.email,
    raw: item,
  });

  private toRecipientOption = (
    item: RecipientListInterface
  ): ExistingUserOption => ({
    id: item.bene_id,
    name: [item.bene_first_name, item.bene_middle_name, item.bene_last_name]
      .filter(Boolean)
      .join(' '),
    email: item.bene_email,
    raw: item,
  });

  formatSelected(
    raw: SenderListInterface | RecipientListInterface | null
  ): string {
    if (!raw || Object.keys(raw).length === 0) return '';
    const option = this.dialogData.popupType
      ? this.toSenderOption(raw as SenderListInterface)
      : this.toRecipientOption(raw as RecipientListInterface);
    return option.email ? `${option.name} - ${option.email}` : option.name;
  }

  private applyFilter(): void {
    const search = (this.senderRecipientFilterCtrl.value ?? '')
      .toString()
      .toLowerCase();
    if (!search) {
      this.filteredOptions.next(this.options.slice());
      return;
    }
    this.filteredOptions.next(
      this.options.filter(
        option =>
          option.name.toLowerCase().includes(search) ||
          option.email.toLowerCase().includes(search)
      )
    );
  }

  closeDialog(): void {
    const result: ExistingUserDialogResult = {
      isSender: this.dialogData.popupType,
      data: (this.senderRecipientCtrl.value ?? {}) as ExistingUserSelection,
    };
    this.closeDialogRef.close(result);
  }

  ngAfterViewInit(): void {
    this.setInitialValue();
  }

  protected setInitialValue(): void {
    this.filteredOptions
      .pipe(take(1), takeUntil(this.#destroy$))
      .subscribe(() => {
        const idKey = this.dialogData.popupType ? 'sender_id' : 'bene_id';
        this.singleSelect.compareWith = (a, b): boolean =>
          !!a && !!b && a[idKey] === b[idKey];
      });
  }

  trackById(_: number, option: ExistingUserOption): number {
    return option.id;
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
